// src/components/CampaignWizard/StepPreview.tsx
import React, { useEffect, useState } from "react";
import {
	Row,
	Col,
	Button,
	ButtonGroup,
	Card,
	CardHeader,
	CardBody,
	Spinner,
} from "reactstrap";
import Select from "react-select";
import { ChannelType } from "./StepChannels"; // reuse your ChannelType

const CHANNEL_CONFIG: Record<ChannelType, { label: string; icon: string }> = {
	EMAIL: { label: "Email", icon: "ri-mail-line" },
	SMS: { label: "SMS", icon: "ri-message-2-line" },
	IN_APP: { label: "In-App", icon: "ri-smartphone-line" },
};

export interface StepPreviewProps {
	data: {
		emailHtml: string;
		smsText: string;
		inApp: { title: string; body: string };
	};
	/** which channels the user turned on in StepChannels */
	enabledChannels: ChannelType[];
	setData: (d: any) => void;
	onNext: () => void;
	onBack: () => void;
}

const StepPreview: React.FC<StepPreviewProps> = ({
	data,
	enabledChannels,
	setData,
	onNext,
	onBack,
}) => {
	// if only one channel, default to that; otherwise first enabled
	const [active, setActive] = useState<ChannelType>(
		enabledChannels.length === 1
			? enabledChannels[0]
			: enabledChannels[0] || "EMAIL"
	);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		// simulate fetching a preview payload
		setTimeout(() => {
			setData({
				emailHtml: "<h4>Hello, {{name}}!</h4><p>This is your email.</p>",
				smsText: "Hi {{name}}, this is your SMS preview.",
				inApp: { title: "Welcome 🎉", body: "Your in-app notification body." },
			});
			setLoading(false);
		}, 300);
	}, [setData]);

	return (
		<>
			<Row className="align-items-center mb-3">
				<Col xs={12} sm={6}>
					<Select
						options={[
							{ value: "1", label: "Alice Johnson" },
							{ value: "2", label: "Bob Smith" },
						]}
						placeholder="Sample recipient…"
						isSearchable={false}
					/>
				</Col>

				{/* only show channel buttons if more than one enabled */}
				{enabledChannels.length > 1 && (
					<Col xs={12} sm={6} className="text-sm-end mt-2 mt-sm-0">
						<ButtonGroup>
							{enabledChannels.map((ch) => (
								<Button
									key={ch}
									outline
									color="primary"
									active={active === ch}
									onClick={() => setActive(ch)}
									style={{ minWidth: 80 }}
								>
									<i className={`${CHANNEL_CONFIG[ch].icon} me-1`} />
									<span className="d-none d-md-inline">
										{CHANNEL_CONFIG[ch].label}
									</span>
								</Button>
							))}
						</ButtonGroup>
					</Col>
				)}
			</Row>

			<Card className="mb-3 shadow-sm">
				<CardHeader className="bg-white">
					<i className={`${CHANNEL_CONFIG[active].icon} me-2`} />
					Viewing: <strong>{CHANNEL_CONFIG[active].label}</strong>
				</CardHeader>
				<CardBody style={{ minHeight: 180, position: "relative" }}>
					{loading ? (
						<div className="text-center py-5">
							<Spinner size="lg" />
						</div>
					) : active === "EMAIL" ? (
						<div
							style={{ overflowY: "auto", maxHeight: 300 }}
							dangerouslySetInnerHTML={{ __html: data.emailHtml }}
						/>
					) : active === "SMS" ? (
						<pre className="m-0" style={{ whiteSpace: "pre-wrap" }}>
							{data.smsText}
						</pre>
					) : (
						<div>
							<h5>{data.inApp.title}</h5>
							<p>{data.inApp.body}</p>
						</div>
					)}
				</CardBody>
			</Card>
		</>
	);
};

export default StepPreview;
